import { Form } from '@panwds/react-form'
import type { VFC } from 'react'
import { useMemo } from 'react'
import type { ComponentDef, ManifestFlags } from '../renderer'
import type { CommonStoreState, ContextApp } from '../store'
import ErrorBanner from './common/ErrorBanner'
import ActivationBanner from './common/ActivationBanner'
import {
  ACTIVATION_TYPE,
  APP_ID_LOGGING_SERVICE,
  CREATE_NEW,
  GLOBAL_REGIONS_APPS,
  VIEW_MANAGE_TENANT
} from './common/GlobalConstants'
import TenancyService from '../services/TenancyService'
import { useCallback, useState, useEffect } from 'react'
import SubscriptionService from '../services/SubscriptionService'
import { pick } from 'lodash'
import { actions, history } from '@sparky/framework'
import { ACTIVATION_UI_ERROR_CODES, ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP } from '../ErrorCodes'
import ScreenLoader from './common/ScreenLoader'
import { appIdsEligibleForActivation } from './common/utils'
import { APP_ID_DSS_ID, TSG_NONE } from '../../common/constants/GlobalConstants'
import { CONTEXT_TO_APP_ID } from '../constants'
import { authStateRef } from '../../activation/store'

type DefaultActivationFormProps = {
  children: ComponentDef
  state: CommonStoreState
  primaryButton: {
    text: string
  }
}


const DefaultActivateForm: VFC<DefaultActivationFormProps> = ({ children, state, primaryButton }) => {
  const { tsgs, licenses, hash, apps, tsg, instances, isHashFullyActivated, manifest, context } = state
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [infoMsg, setInfoMsg] = useState<string>('')
  const [activationInProgress, setActivationInProgress] = useState(false)
  const { current } = authStateRef

  //const cdlActivatedInfoMsg = 'CDL license in this magic link is already activated. To proceed with the activation, please select a TSG which has Cortex Data Lake service running!'
  const cdlNotActivatedInfoMsg = 'The selected TSG already has Cortex Data Lake service running. To proceed with the activation, please select a TSG which does NOT have a CDL service running!'

  const ContextToAppId: ContextApp = CONTEXT_TO_APP_ID

  const appIdsToBeActivated = useMemo(() => {
    return appIdsEligibleForActivation(licenses, manifest?.primary_app_id as string, manifest?.flags as ManifestFlags)
  }, [licenses, manifest])

  useEffect(() => {
    const tsgCdlInstance = instances?.get(APP_ID_LOGGING_SERVICE)
    if (isHashFullyActivated) {
      setInfoMsg(ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP.HASH_FULLY_ACTIVATED)
    }
    else if (appIdsToBeActivated.includes(APP_ID_LOGGING_SERVICE) && tsgCdlInstance) {
      setInfoMsg(cdlNotActivatedInfoMsg)
    }
    else {
      setInfoMsg('')
    }
  }, [licenses, tsg, setInfoMsg, instances, isHashFullyActivated, appIdsToBeActivated])

  const calculateNewTsg = useCallback((currentTsgName: string, tsgName: string, tsgDropDownDetails: any, count = 0): string => {
    const isNewTsgName = tsgDropDownDetails.some((each: any) => {
      return each.display_name === tsgName
    })
    if (!isNewTsgName) {
      return tsgName
    }
    else {
      count++
      return calculateNewTsg(currentTsgName, `${currentTsgName} - ${count}`, tsgDropDownDetails, count)
    }
  }, [])

  const getInstanceObject = useCallback((elem: any, support_account_id: string, props: any) => {
    const isAmend = (apps.get(elem.app_id)?.device_association?.license || manifest?.flags?.amend_license) && instances?.find((each) => each.app_id === elem.app_id)
    const response: any = {
      'app_id': elem.app_id,
      'support_account_id': support_account_id,
      'activation_type': isAmend ? ACTIVATION_TYPE.AMEND : ACTIVATION_TYPE.NEW,
      ...(context && elem.app_id === ContextToAppId[context] && manifest?.flags?.sub_domain_required && { subdomain: props.subdomain })
    }
    if (!apps.get(elem.app_id)?.device_association?.license && elem.app_id !== APP_ID_DSS_ID) {
      response.allocation = elem.licenses.map((each: any) => {
        return {
          ...pick(each, ['app_id', 'license_type']),
          allocation_size: each.purchased_size
        }
      })
    }
    return response
  }, [ContextToAppId, apps, context, instances, manifest])

  const formatInputRequest = useCallback((props: any) => {
    const { tsg_name, region, support_account_id, cdl, cie, subdomain, migrate_csp_instance, appAddons, description } = props
    const instances: any[] = []
    const inputRequest: {
      is_eula_accepted: boolean;
      tsg_name: any;
      hash: string | undefined;
      platform_region: any;
      allocation_type: string;
      instances: any[];
      description: any;
      migrate_csp_instance?: {} | null
    } = {
      'is_eula_accepted': true,
      'tsg_name': tsg_name,
      'hash': hash,
      'platform_region': region ?? GLOBAL_REGIONS_APPS,
      'allocation_type': 'FULL',
      'description': description,
      instances,
      migrate_csp_instance: {}
    }
    if (licenses) {
      licenses.forEach((each: any) => {
        if (appIdsToBeActivated.includes(each.app_id) && !(each.app_id === APP_ID_LOGGING_SERVICE && cdl?.description === 'existing instance')) {
          instances.push(getInstanceObject(each, support_account_id?.toString(), props))
        }
      })
      if (cie?.value === CREATE_NEW) {
        instances.push(getInstanceObject({ app_id: APP_ID_DSS_ID }, support_account_id?.toString(), props))
      }
      else if (cie?.value !== 'None') {
        if (state?.activationData?.instances && state?.activationData?.instances[APP_ID_DSS_ID]) {
          instances.push({ ...state?.activationData?.instances[APP_ID_DSS_ID], support_account_id: support_account_id?.toString() })
        }
      }
    }
    else {
      appIdsToBeActivated?.forEach(app_id => {
        instances.push({
          app_id,
          support_account_id: support_account_id?.toString(),
          activation_type: 'NEW',
          ...(context && app_id === ContextToAppId[context] && manifest?.flags?.sub_domain_required && { subdomain })
        })
      })
    }
    if (appAddons?.length) { // add app addons to final instances list
      appAddons.forEach((each: { app_id: string, required_x_signature: boolean }) => {
        const signature = state.supportAccounts?.find(e => e.accountid === support_account_id)?.signature
        const notonorafter = current?.user?.notonorafter
        const x_sig = `${support_account_id} ${notonorafter} ${signature}`
        instances.push({
          app_id: each.app_id,
          support_account_id: support_account_id?.toString(),
          ...(each.required_x_signature && { 'x-sig': x_sig }),
          activation_type: 'NEW',
        })
      })
    }

    if (migrate_csp_instance && migrate_csp_instance?.id !== 'new') {
      inputRequest.migrate_csp_instance = {
        tenant_ids: [migrate_csp_instance.id]
      }
    }
    else {
      delete inputRequest['migrate_csp_instance']
    }
    return inputRequest
  }, [hash, licenses, appIdsToBeActivated, getInstanceObject,
    state?.activationData?.instances, state.supportAccounts, context, ContextToAppId,
    manifest?.flags?.sub_domain_required, current?.user?.notonorafter])

  const onPrimaryBtnClick = useCallback(async (values: any) => {
    const tsgData = {
      tsg_id: values.tsg.id,
      tsg_name: values.tsg.value,
      isError: false
    }
    setActivationInProgress(true)
    if (values.tsg.value === CREATE_NEW) {
      const tsgDropDownDetails = tsgs?.children?.filter((each: any) => each.isAbsoluteRoot)
      const newTsgName = calculateNewTsg(values['csp'].value, values['csp'].value, tsgDropDownDetails)
      await TenancyService.createTenant.post({ display_name: newTsgName }, TSG_NONE.id).then(({ data }) => {
        tsgData.tsg_id = data.id
        tsgData.tsg_name = data.display_name
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Err', err)
        if (err?.status === 400) {
          setErrorMsg(err?.error?.message || ACTIVATION_UI_ERROR_CODES.CREATE_TENANT_FAILED)
        }
        else {
          setErrorMsg(ACTIVATION_UI_ERROR_CODES.CREATE_TENANT_FAILED)
        }
        setActivationInProgress(false)
        tsgData.isError = true
      })
    }
    if (!tsgData.isError) {
      const request = [formatInputRequest({
        tsg_name: tsgData.tsg_name,
        region: values['region']?.id,
        support_account_id: values['csp']?.id,
        cdl: values['cdl'],
        cie: values['cie'],
        subdomain: values['subdomain'],
        appAddons: values['appAddons'],
        description: values['description'],
        migrate_csp_instance: values['migrate_csp_instance'],
      })]
      SubscriptionService.activateInstance.post(request, tsgData.tsg_id).then(async (data) => {
        await actions.loadTsgInstances(tsgData.tsg_id)
        if (values.tsg.value === CREATE_NEW) {
          await actions.loadTsgTree(tsgData.tsg_id)
        }
        await actions.switchTsg(tsgData.tsg_id)
        setActivationInProgress(false)
        history.push('/')
        actions.openCommonModule(VIEW_MANAGE_TENANT, tsgData.tsg_id)
      }).catch((err) => {
        setActivationInProgress(false)
        setErrorMsg(err?.message || ACTIVATION_UI_ERROR_CODES.ACTIVATION_FAILED)
      })
    }
  }, [calculateNewTsg, formatInputRequest, tsgs?.children])

  const clearErrorMessage = useCallback(() => {
    setErrorMsg('')
  }, [])

  const groupedChildren = useMemo(() => {
    const subChildren = children?.props?.children
    const groupedChilderen = new Map()
    if (subChildren && Array.isArray(subChildren)) {
      subChildren.forEach(child => {
        const key = child?.props?.group || 1
        const collection = groupedChilderen.get(key) || []
        if (collection.length) {
          collection.push(child)
        }
        else {
          groupedChilderen.set(key, [child])
        }
      })
    }
    else {
      groupedChilderen.set(1, subChildren)
    }
    return groupedChilderen
  }, [children])

  return <div>
    {activationInProgress && <ScreenLoader />}
    {infoMsg && <ActivationBanner infoMessage={infoMsg} appearance='standard' type='page' />}
    {errorMsg && <ErrorBanner errorCode={errorMsg} callback={clearErrorMessage} />}
    <div className='tw-flex tw-flex-row'>
      <Form onSubmit={onPrimaryBtnClick} mode='onBlur' formProps={{ style: { height: '100%', width: '100%' } }}>
        <div className='tw-p-6 tw-border-solid cm-border-[#DADBDB] dark:cm-border-[#242424]'>
          {groupedChildren.get(1)}
        </div>
        {groupedChildren.has(2) && <div className='tw-mt-2'>
          {groupedChildren.get(2)}
        </div>}
        {groupedChildren.has(3) && <div className='tw-mt-3'>
          {groupedChildren.get(3)}
        </div>}
      </Form>
    </div>
  </div>
}
export default DefaultActivateForm

